// src/services/firebaseAuth.service.ts
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { auth } from '../config/firebase.config';
import { Capacitor } from '@capacitor/core';
import { UserProfile } from '@/types';

export interface FirebaseUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
  metadata?: {
    creationTime?: string;
  };
}

class FirebaseAuthService {
  async signIn(email: string, password: string): Promise<FirebaseUser> {
    try {
      console.log('[FirebaseAuth] Starting email/password sign in...');
      
      if (Capacitor.isNativePlatform()) {
        // Use native Firebase SDK
        console.log('[FirebaseAuth] Using native Firebase authentication');
        const result = await FirebaseAuthentication.signInWithEmailAndPassword({
          email,
          password
        });
        
        if (result.user) {
          console.log('[FirebaseAuth] Native sign in successful:', result.user.email);
          return this.mapUser(result.user);
        } else {
          throw new Error('No user returned from native authentication');
        }
      } else {
        // Web fallback
        console.log('[FirebaseAuth] Using web Firebase authentication');
        const { signInWithEmailAndPassword } = await import('firebase/auth');
        const result = await signInWithEmailAndPassword(auth, email, password);
        
        console.log('[FirebaseAuth] Web sign in successful:', result.user.email);
        return this.mapUser(result.user);
      }
    } catch (error) {
      console.error('[FirebaseAuth] Sign in error:', error);
      throw error;
    }
  }

  async signOut(): Promise<void> {
    try {
      console.log('[FirebaseAuth] Starting sign out...');
      
      if (Capacitor.isNativePlatform()) {
        await FirebaseAuthentication.signOut();
        console.log('[FirebaseAuth] Native sign out successful');
      } else {
        await auth.signOut();
        console.log('[FirebaseAuth] Web sign out successful');
      }
      
    } catch (error) {
      console.error('[FirebaseAuth] Sign out error:', error);
      throw error;
    }
  }

  async forceClearAuth(): Promise<void> {
    try {
      console.log('[FirebaseAuth] Force clearing authentication state...');
      
      // Multiple sign outs to clear keychain
      for (let i = 0; i < 3; i++) {
        try {
          if (Capacitor.isNativePlatform()) {
            await FirebaseAuthentication.signOut();
          } else {
            await auth.signOut();
          }
        } catch (e) {
          // Ignore errors during force clear
        }
      }
      
      // Clear any cached tokens
      if (Capacitor.isNativePlatform()) {
        // Try to clear any stored tokens
        try {
          await FirebaseAuthentication.signOut();
        } catch (e) {
          // Ignore
        }
      }
      
      console.log('[FirebaseAuth] Force clear completed');
    } catch (error) {
      console.error('[FirebaseAuth] Force clear error:', error);
      // Don't throw - we want to continue even if this fails
    }
  }

  async getCurrentUser(): Promise<FirebaseUser | null> {
    try {
      if (Capacitor.isNativePlatform()) {
        const result = await FirebaseAuthentication.getCurrentUser();
        return result.user ? this.mapUser(result.user) : null;
      } else {
        const user = auth.currentUser;
        return user ? this.mapUser(user) : null;
      }
    } catch (error) {
      console.error('[FirebaseAuth] Get current user error:', error);
      return null;
    }
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      const user = await this.getCurrentUser();
      return user !== null;
    } catch (error) {
      console.error('[FirebaseAuth] Check authentication error:', error);
      return false;
    }
  }

  async getIdToken(): Promise<string | null> {
    try {
      if (Capacitor.isNativePlatform()) {
        const result = await FirebaseAuthentication.getIdToken();
        return result.token;
      } else {
        const user = auth.currentUser;
        return user ? await user.getIdToken() : null;
      }
    } catch (error) {
      console.error('[FirebaseAuth] Get ID token error:', error);
      return null;
    }
  }

  // Email/Password authentication methods
  async signInWithEmail(email: string, password: string): Promise<FirebaseUser> {
    try {
      console.log('[FirebaseAuth] Signing in with email...');
      
      if (Capacitor.isNativePlatform()) {
        const result = await FirebaseAuthentication.signInWithEmailAndPassword({
          email,
          password
        });
        
        if (result.user) {
          return this.mapUser(result.user);
        } else {
          throw new Error('No user returned from email authentication');
        }
      } else {
        const { signInWithEmailAndPassword } = await import('firebase/auth');
        const result = await signInWithEmailAndPassword(auth, email, password);
        return this.mapUser(result.user);
      }
    } catch (error) {
      console.error('[FirebaseAuth] Email sign in error:', error);
      throw error;
    }
  }

  async signUpWithEmail(email: string, password: string): Promise<FirebaseUser> {
    try {
      console.log('[FirebaseAuth] Signing up with email...');
      
      let user: FirebaseUser;
      if (Capacitor.isNativePlatform()) {
        const result = await FirebaseAuthentication.createUserWithEmailAndPassword({
          email,
          password
        });
        
        if (result.user) {
          user = this.mapUser(result.user);
        } else {
          throw new Error('No user returned from email signup');
        }
      } else {
        const { createUserWithEmailAndPassword } = await import('firebase/auth');
        const result = await createUserWithEmailAndPassword(auth, email, password);
        user = this.mapUser(result.user);
      }

      const skeletonProfile: UserProfile = {
        uid: user.uid,
        email: user.email || '',
        createdAt: Date.now(),
      };
      localStorage.setItem('lotteryOfLife_userProfile', JSON.stringify(skeletonProfile));

      return user;
    } catch (error) {
      console.error('[FirebaseAuth] Email signup error:', error);
      throw error;
    }
  }

  async updateDisplayName(displayName: string): Promise<void> {
    try {
      if (Capacitor.isNativePlatform()) {
        // Capacitor plugin does not expose profile update; fallback to web SDK
        const { updateProfile } = await import('firebase/auth');
        if (!auth.currentUser) throw new Error('Not authenticated');
        await updateProfile(auth.currentUser, { displayName });
      } else {
        const { updateProfile } = await import('firebase/auth');
        if (!auth.currentUser) throw new Error('Not authenticated');
        await updateProfile(auth.currentUser, { displayName });
      }
    } catch (error) {
      console.error('[FirebaseAuth] updateDisplayName error:', error);
      throw error;
    }
  }

  async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      console.log('[FirebaseAuth] Sending password reset email for:', email);
      
      // Always use web implementation for password reset
      // The native plugin doesn't support this method properly
      const { sendPasswordResetEmail } = await import('firebase/auth');
      await sendPasswordResetEmail(auth, email);
      console.log('[FirebaseAuth] Password reset email sent successfully');
      
    } catch (error: any) {
      console.error('[FirebaseAuth] Password reset error:', error);
      
      // Provide user-friendly error messages
      if (error.code === 'auth/user-not-found') {
        throw new Error('No account found with this email address.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Please enter a valid email address.');
      } else {
        throw new Error('Failed to send password reset email. Please try again.');
      }
    }
  }

  // Helper method to map Firebase user to our interface
  private mapUser(user: any): FirebaseUser {
    return {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      emailVerified: user.emailVerified || false,
      metadata: {
        creationTime: user.metadata?.creationTime,
      }
    };
  }

  // Access validation methods (keeping the same interface as before)
  async validateUserAccess(token: string): Promise<{ isValid: boolean; isExpired: boolean; remainingDays: number; subscriptionTier: 'free' | 'premium'; livesGenerated: number }> {
    console.log('[FirebaseAuth] Validating user access...');
    
    if (!token) {
      console.error('[FirebaseAuth] No token provided for validation');
      return this.getDefaultValidation();
    }

    console.log('[FirebaseAuth] Token exists, using client-side validation');
    return this.fallbackValidation();
  }

  private fallbackValidation(): { isValid: boolean; isExpired: boolean; remainingDays: number; subscriptionTier: 'free' | 'premium'; livesGenerated: number } {
    console.log('[FirebaseAuth] Executing client-side validation');
    
    try {
      const savedUser = localStorage.getItem('lotteryOfLife_user');
      console.log('[FirebaseAuth] Stored user data:', savedUser ? 'exists' : 'not found');
      
      if (!savedUser) {
        console.log('[FirebaseAuth] No stored user, creating default demo access');
        return this.createDemoAccess();
      }

      const user = JSON.parse(savedUser);
      console.log('[FirebaseAuth] Parsed user data:', user);
      
      if (!user.demoExpiresAt) {
        console.log('[FirebaseAuth] No demo expiration set, creating new demo access');
        return this.createDemoAccess();
      }

      const now = new Date();
      const expiresAt = new Date(user.demoExpiresAt);
      const isExpired = now > expiresAt;

      const remainingDays = isExpired
        ? 0
        : Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      console.log('[FirebaseAuth] Demo status:', {
        expiresAt: expiresAt.toISOString(),
        isExpired,
        remainingDays
      });

      return {
        isValid: !isExpired,
        isExpired,
        remainingDays,
        subscriptionTier: user.subscriptionTier || 'free',
        livesGenerated: user.livesGenerated || 0
      };
    } catch (error) {
      console.error('[FirebaseAuth] Client-side validation failed:', error);
      return this.getDefaultValidation();
    }
  }

  private createDemoAccess(): { isValid: boolean; isExpired: boolean; remainingDays: number; subscriptionTier: 'free' | 'premium'; livesGenerated: number } {
    console.log('[FirebaseAuth] Creating new demo access');
    
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 30);
    
    const demoUser = {
      demoExpiresAt: expiresAt.toISOString(),
      subscriptionTier: 'free',
      livesGenerated: 0,
      createdAt: new Date().toISOString()
    };
    
    localStorage.setItem('lotteryOfLife_user', JSON.stringify(demoUser));
    
    console.log('[FirebaseAuth] Demo access created, expires:', expiresAt.toISOString());
    
    return {
      isValid: true,
      isExpired: false,
      remainingDays: 30,
      subscriptionTier: 'free',
      livesGenerated: 0
    };
  }

  private getDefaultValidation(): { isValid: boolean; isExpired: boolean; remainingDays: number; subscriptionTier: 'free' | 'premium'; livesGenerated: number } {
    console.log('[FirebaseAuth] Returning default (invalid) validation');
    return {
      isValid: false,
      isExpired: true,
      remainingDays: 0,
      subscriptionTier: 'free',
      livesGenerated: 0
    };
  }

  /**
   * Listen to auth state changes
   */
  onAuthStateChanged(callback: (user: FirebaseUser | null) => void): () => void {
    console.log('[FirebaseAuth] Setting up auth state listener');
    
    if (Capacitor.isNativePlatform()) {
      // For native platforms, we'll use Capacitor Firebase Authentication with polling
      let unsubscribed = false;
      let currentUser: FirebaseUser | null = null;
      
      const checkAuthState = async () => {
        if (unsubscribed) return;
        
        try {
          const result = await FirebaseAuthentication.getCurrentUser();
          const user = result.user ? this.mapUser(result.user) : null;
          
          // Only call callback if user state actually changed
          const userChanged = (currentUser?.uid !== user?.uid);
          if (userChanged) {
            console.log('[FirebaseAuth] Native auth state changed:', user ? { uid: user.uid, email: user.email } : null);
            currentUser = user;
            callback(user);
          }
        } catch (error) {
          // User is not authenticated
          if (currentUser !== null) {
            console.log('[FirebaseAuth] Native auth state changed: user signed out');
            currentUser = null;
            callback(null);
          }
        }
      };
      
      // Initial check
      checkAuthState();
      
      // Poll for auth state changes every 30 seconds (much less aggressive)
      // This is needed because Capacitor Firebase doesn't have real-time auth state listeners
      const pollInterval = setInterval(checkAuthState, 120000); // Poll every 2 minutes
      
      return () => {
        unsubscribed = true;
        clearInterval(pollInterval);
      };
    } else {
      // Web implementation using Firebase auth
      try {
        // Use the already imported auth from config
        const { onAuthStateChanged } = require('firebase/auth');
        return onAuthStateChanged(auth, (user: any) => {
          console.log('[FirebaseAuth] Web auth state changed:', user ? { uid: user.uid, email: user.email } : null);
          callback(user ? this.mapUser(user) : null);
        });
      } catch (error) {
        console.error('[FirebaseAuth] Error setting up auth state listener:', error);
        return () => {};
      }
    }
  }
}

export const firebaseAuthService = new FirebaseAuthService();
